/**
 * useWcSigningRequest — resolution for client-signing (WalletConnect) wallets
 * (multi-wallet Phase 4).
 *
 * Flow: send the intent's txJson to the wallet over the WC session (the wallet
 * signs AND broadcasts — spike-verified Joey behavior), then report the returned
 * hash to POST /signing-intents/:id/submitted, which verifies ON-CHAIN before the
 * UI may treat the transaction as real. onSigned only fires after that
 * server-side verification.
 *
 * StrictMode-safe: signing is keyed to payload.id via a module-independent ref so a
 * dev-mode mount/cleanup/remount cycle neither double-signs nor discards the in-flight
 * result. The wallet broadcasts irreversibly, so we must always process the response of
 * a request we actually sent — we only suppress STATE updates after a real payload
 * change, never the signing itself.
 */

import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiHttpError } from '../api/client';
import { wcSignTransaction } from '../lib/wallet/wcAdapter';
import type { SigningPayload, SigningState } from '../types/signing';

export interface UseWcSigningRequestOptions {
  payload: SigningPayload;
  onSigned?: (txid: string) => void;
  onRejected?: () => void;
  /** message carries the specific outcome (e.g. the soft "broadcast but unconfirmed"
   *  notice) so callers can show it instead of a generic "expired". */
  onExpired?: (message?: string) => void;
}

export function useWcSigningRequest({ payload, onSigned, onRejected, onExpired }: UseWcSigningRequestOptions): SigningState {
  const [state, setState] = useState<SigningState>({ status: 'pending', isWebSocket: false });
  // Which payload.id we've already kicked off signing for (survives StrictMode remounts).
  const signingForRef = useRef<string | null>(null);
  // Guard against firing a terminal callback more than once per payload.
  const settledForRef = useRef<string | null>(null);
  // Latest callbacks, so the long-lived signing promise never calls stale closures.
  const cbRef = useRef({ onSigned, onRejected, onExpired });
  cbRef.current = { onSigned, onRejected, onExpired };

  useEffect(() => {
    if (signingForRef.current === payload.id) return; // already signing/signed this payload
    signingForRef.current = payload.id;
    const thisId = payload.id;

    // True only while THIS payload is the one being displayed; a payload change flips it.
    const isCurrent = () => signingForRef.current === thisId;

    const settle = (status: 'signed' | 'rejected' | 'expired', extra: Partial<SigningState>, cb?: () => void) => {
      if (!isCurrent()) return;
      setState({ status, isWebSocket: false, ...extra });
      if (settledForRef.current === thisId) return;
      settledForRef.current = thisId;
      cb?.();
    };

    (async () => {
      try {
        if (!payload.txJson) {
          settle('rejected', { error: 'Signing request is missing transaction data' }, () => cbRef.current.onRejected?.());
          return;
        }
        if (isCurrent()) setState({ status: 'signing', isWebSocket: false });

        // The wallet signs AND broadcasts here — irreversible once it returns.
        const result = await wcSignTransaction(payload.txJson);
        if (!result.hash) {
          settle('rejected', { error: 'Wallet did not return a transaction hash' }, () => cbRef.current.onRejected?.());
          return;
        }

        if (isCurrent()) setState({ status: 'dispatching', isWebSocket: false });

        // Have the backend verify on-chain before trusting the client-reported hash.
        // The wallet ALWAYS broadcasts (spike-verified), so the tx WILL land. We must be
        // careful what we call a failure:
        //   - 'rejected' is reserved for a DEFINITIVE on-chain verdict — the 422 verifier
        //     reason says the tx failed/mismatched (tef/tec/field mismatch), not merely
        //     "still confirming".
        //   - Transient problems on OUR side (network blip reaching the API, 5xx) must NOT
        //     be reported as a rejection — the tx is already on its way to the ledger, so
        //     we keep retrying, and only fall back to a soft "couldn't confirm" if we run
        //     out of attempts.
        let verifiedOk = false;
        let definitiveFailure = false;
        let lastMessage = 'Transaction could not be verified on the ledger';
        for (let attempt = 0; attempt < 8 && isCurrent(); attempt++) {
          let resp: any;
          try {
            resp = await apiClient.post<any>(`/signing-intents/${thisId}/submitted`, { tx_hash: result.hash });
          } catch (e) {
            // The verifier returns 422 with the verdict — that's the ONLY definitive
            // signal. Every other HTTP error (429 rate-limit, 408/425, 5xx) and any
            // transport error is transient on OUR side; the wallet already broadcast, so
            // we keep waiting rather than mislabel a landing tx as rejected.
            if (e instanceof ApiHttpError && e.status === 422) {
              resp = { success: false, message: e.message };
            } else {
              lastMessage = 'Verifying on-chain…';
              await new Promise((r) => setTimeout(r, 4000));
              continue;
            }
          }
          if (resp?.success && resp?.data?.verified) {
            verifiedOk = true;
            break;
          }
          lastMessage = resp?.message ?? lastMessage;
          if (!/confirming|not yet|retry/i.test(lastMessage)) {
            definitiveFailure = true; // a real on-chain verdict, not "still settling"
            break;
          }
          await new Promise((r) => setTimeout(r, 4000));
        }

        if (verifiedOk) {
          settle('signed', { txid: result.hash }, () => cbRef.current.onSigned?.(result.hash!));
        } else if (definitiveFailure) {
          settle('rejected', { error: lastMessage }, () => cbRef.current.onRejected?.());
        } else {
          // Broadcast succeeded but we couldn't confirm (transient errors / ran out of
          // attempts). Don't claim it failed — surface a soft, non-destructive outcome so
          // the user checks their history rather than re-signing a tx that likely landed.
          const softMsg =
            'Transaction was broadcast but could not be confirmed yet. Check your wallet history before retrying.';
          settle('expired', { error: softMsg }, () => cbRef.current.onExpired?.(softMsg));
        }
      } catch (err) {
        const msg =
          err instanceof ApiHttpError
            ? err.message || `Transaction verification failed (${err.status})`
            : err instanceof Error
              ? err.message
              : 'Signing failed';
        if (/timed out/i.test(msg)) {
          settle('expired', { error: msg }, () => cbRef.current.onExpired?.(msg));
        } else if (/reject|denied|declin|cancell/i.test(msg)) {
          settle('rejected', { error: 'User rejected the transaction' }, () => cbRef.current.onRejected?.());
        } else {
          settle('rejected', { error: msg }, () => cbRef.current.onRejected?.());
        }
      }
    })();
    // Intentionally no cleanup that cancels the in-flight result: the wallet has
    // already broadcast, so the response of a request we sent must always be handled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.id, payload.txJson]);

  return state;
}
